// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import SearchFieldWrapper, { SearchFieldWrapperStyles } from "@steiner/App/Components/ReorderPad/SearchFieldWrapper";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@steiner/MobiusOverrides/DatePicker";
import React, { FC } from "react";

export interface SearchFieldDateRangeProps {
    fromDate?: Date;
    toDate?: Date;
    fromDateChangeHandler?: ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => void;
    toDateChangeHandler?: ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => void;
    extendedStyles?: SearchFieldDateRangeStyles;
}

export interface SearchFieldDateRangeStyles {
    searchFieldWrapper?: SearchFieldWrapperStyles;
    datePickersGridContainer?: GridContainerProps;
    datePickersGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    label?: TypographyPresentationProps;
    toDate?: DatePickerPresentationProps;
}

export const searchFieldDateRangeStyles: SearchFieldDateRangeStyles = {};

const SearchFieldDateRange: FC<SearchFieldDateRangeProps> = React.memo(
    ({ fromDate, fromDateChangeHandler, toDate, toDateChangeHandler, extendedStyles }) => {
        const [styles] = React.useState(() => mergeToNew(searchFieldDateRangeStyles, extendedStyles));

        return (
            <GridItem {...styles.datePickersGridItem} data-test-selector="reorderPad_filterDateRange">
                <Typography {...styles.label}> {translate("Date Range")}</Typography>

                <GridContainer {...styles.datePickersGridContainer}>
                    <SearchFieldWrapper extendedStyles={styles.searchFieldWrapper}>
                        <DatePicker
                            data-test-selector="reorderPad_filterFromDate"
                            selectedDay={fromDate}
                            onDayChange={fromDateChangeHandler}
                            dateTimePickerProps={{
                                clearIcon: null,
                                maxDate: toDate,
                                ...styles.fromDate?.dateTimePickerProps,
                            }}
                            {...styles.fromDate}
                        />
                    </SearchFieldWrapper>
                    <SearchFieldWrapper extendedStyles={styles.searchFieldWrapper}>
                        <DatePicker
                            selectedDay={toDate}
                            onDayChange={toDateChangeHandler}
                            dateTimePickerProps={{
                                clearIcon: null,
                                minDate: fromDate,
                                ...styles.toDate?.dateTimePickerProps,
                            }}
                            data-test-selector="reorderPad_filterToDate"
                            {...styles.toDate}
                        />
                    </SearchFieldWrapper>
                </GridContainer>
            </GridItem>
        );
    },
);

export default SearchFieldDateRange;
